import { LSATransaction, ExportOptions } from '../types';

export interface ExportProgress {
  current: number;
  total: number;
  percentage: number;
  status: 'fetching' | 'processing' | 'complete' | 'error';
  message: string;
}

export interface ExportResult {
  success: boolean;
  filename?: string;
  error?: string;
}

export class ExcelExportService {
  private static instance: ExcelExportService;
  private abortController: AbortController | null = null;
  private xlsxModule: typeof import("xlsx") | null = null;

  private async getXLSX() {
    if (!this.xlsxModule) {
      this.xlsxModule = await import("xlsx");
    }
    return this.xlsxModule;
  }

  static getInstance(): ExcelExportService {
    if (!ExcelExportService.instance) {
      ExcelExportService.instance = new ExcelExportService();
    }
    return ExcelExportService.instance;
  }

  /**
   * Export transactions to Excel with progress tracking
   */
  async exportTransactions(
    transactions: LSATransaction[],
    options: ExportOptions,
    onProgress?: (progress: ExportProgress) => void,
    groupName = 'LSA Group'
  ): Promise<ExportResult> {
    try {
      // Initialize progress
      onProgress?.({
        current: 0,
        total: transactions.length,
        percentage: 0,
        status: 'processing',
        message: 'Preparing export...'
      });

      // Prepare data based on selected columns
      const exportData = this.prepareExportData(transactions, options);

      // Lazy-load XLSX only when needed to avoid upfront bundle cost
      const XLSX = await this.getXLSX();

      // Update progress
      onProgress?.({
        current: transactions.length,
        total: transactions.length,
        percentage: 50,
        status: 'processing',
        message: 'Generating Excel file...'
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = this.calculateColumnWidths(exportData);
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

      // Generate filename
      const [timestamp,] = new Date().toISOString().split('T');
      const filename = `LSA_Transactions_${groupName.replace(/\s+/g, '_')}_${timestamp}.xlsx`;

      // Update progress
      onProgress?.({
        current: transactions.length,
        total: transactions.length,
        percentage: 90,
        status: 'processing',
        message: 'Finalizing export...'
      });

      // Write file
      XLSX.writeFile(workbook, filename);

      // Complete
      onProgress?.({
        current: transactions.length,
        total: transactions.length,
        percentage: 100,
        status: 'complete',
        message: 'Export completed successfully!'
      });

      return {
        success: true,
        filename
      };

    } catch (error) {
      console.error('Export failed:', error);
      
      onProgress?.({
        current: 0,
        total: transactions.length,
        percentage: 0,
        status: 'error',
        message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Export all transactions with batch fetching
   */
  async exportAllTransactions(
    fetchFunction: (page: number, limit: number) => Promise<{ items: LSATransaction[], totalCount: number }>,
    options: ExportOptions,
    onProgress?: (progress: ExportProgress) => void,
    groupName = 'LSA Group'
  ): Promise<ExportResult> {
    this.abortController = new AbortController();
    
    try {
      const batchSize = 50;
      let allTransactions: LSATransaction[] = [];
      let currentPage = 1;
      let totalCount = 0;

      // First fetch to get total count
      onProgress?.({
        current: 0,
        total: 0,
        percentage: 0,
        status: 'fetching',
        message: 'Fetching transaction count...'
      });

      const firstBatch = await fetchFunction(1, batchSize);
      ({ totalCount } = firstBatch);
      allTransactions = [...firstBatch.items];
      currentPage = 2;

      // Update progress
      onProgress?.({
        current: allTransactions.length,
        total: totalCount,
        percentage: Math.round((allTransactions.length / totalCount) * 30),
        status: 'fetching',
        message: `Fetching transactions... ${allTransactions.length}/${totalCount}`
      });

      // Fetch remaining batches
      while (allTransactions.length < totalCount && !this.abortController.signal.aborted) {
        const batch = await fetchFunction(currentPage, batchSize);
        allTransactions = [...allTransactions, ...batch.items];
        currentPage++;

        const percentage = Math.round((allTransactions.length / totalCount) * 30);
        onProgress?.({
          current: allTransactions.length,
          total: totalCount,
          percentage,
          status: 'fetching',
          message: `Fetching transactions... ${allTransactions.length}/${totalCount}`
        });

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (this.abortController.signal.aborted) {
        throw new Error('Export cancelled by user');
      }

      // Export the collected data
      return await this.exportTransactions(allTransactions, options, onProgress, groupName);

    } catch (error) {
      console.error('Batch export failed:', error);
      
      onProgress?.({
        current: 0,
        total: 0,
        percentage: 0,
        status: 'error',
        message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Cancel ongoing export
   */
  cancelExport(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Estimate export time based on transaction count
   */
  estimateExportTime(transactionCount: number): string {
    const batches = Math.ceil(transactionCount / 50);
    const estimatedSeconds = batches * 1.5; // 1.5 seconds per batch
    
    if (estimatedSeconds < 60) {
      return `~${Math.round(estimatedSeconds)} seconds`;
    } else {
      const minutes = Math.round(estimatedSeconds / 60);
      return `~${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Prepare data for export based on selected columns
   */
  private prepareExportData(transactions: LSATransaction[], options: ExportOptions): any[] {
    return transactions.map(transaction => {
      const row: any = {};

      if (options.columns.memberName) {
        row['Member Name'] = `${transaction.firstName} ${transaction.lastName}`;
      }

      if (options.columns.brandName) {
        row['Brand Name'] = transaction.brandName;
      }

      if (options.columns.category) {
        row['Category'] = 'Fitness & Sports'; // TODO: Get actual category name
      }

      if (options.columns.totalAmount) {
        row['Total Amount'] = transaction.costAmount;
      }

      if (options.columns.reimbursedAmount) {
        row['Reimbursed Amount'] = transaction.coveredAmount.value;
      }

      if (options.columns.processingFee) {
        row['Processing Fee'] = transaction.costAmount - transaction.coveredAmount.value;
      }

      if (options.columns.receiptLink) {
        row['Receipt Link'] = transaction.receiptS3URL;
      }

      if (options.columns.dateSubmitted) {
        row['Date Submitted'] = new Date(transaction.receiptDate).toLocaleDateString();
      }

      if (options.columns.status) {
        row['Status'] = transaction.status ? transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1) : '';
      }

      return row;
    });
  }

  /**
   * Calculate optimal column widths
   */
  private calculateColumnWidths(data: any[]): XLSX.ColInfo[] {
    if (data.length === 0) return [];

    const columns = Object.keys(data[0]);
    return columns.map(col => {
      const maxLength = Math.max(
        col.length,
        ...data.map(row => String(row[col] || '').length)
      );
      
      return {
        wch: Math.min(Math.max(maxLength + 2, 10), 50) // Min 10, Max 50 characters
      };
    });
  }
}

// Export singleton instance
export const excelExportService = ExcelExportService.getInstance();
